import { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MatchCard from '@/components/feature/MatchCard';
import { getMatchesByLeague, leagueMeta, leagueOrder, setPageMeta, sortByTime, type LeagueKey } from '@/lib/site';

const PAGE_SIZE = 6;

const statusFilters = [
  { key: 'all', label: '全部' },
  { key: 'live', label: '正在直播' },
  { key: 'upcoming', label: '即将开始' },
  { key: 'finished', label: '已结束' },
];

const timeFilters = [
  { key: 'all', label: '全部时间' },
  { key: 'today', label: '今日' },
  { key: 'week', label: '本周' },
];

const leagueKeys = leagueOrder as string[];

export default function CategoryPage() {
  const { league } = useParams<{ league: string }>();
  const [status, setStatus] = useState<string>('all');
  const [time, setTime] = useState<string>('all');
  const [visible, setVisible] = useState<number>(PAGE_SIZE);
  const [panelOpen, setPanelOpen] = useState(false);

  const validLeague = league && leagueKeys.includes(league) ? (league as LeagueKey) : null;

  useEffect(() => {
    if (!validLeague) return;
    const meta = leagueMeta[validLeague];
    const liveCount = getMatchesByLeague(validLeague).filter((m) => m.status === 'live').length;
    setPageMeta(
      `${meta.name}赛事直播 - 篮球直播`,
      `${meta.name} 最新赛事直播与赛程：${meta.intro.slice(0, 60)}今日有 ${liveCount} 场正在直播，查看开赛时间与直播状态。`,
      `${meta.name}直播,${meta.name}赛程,篮球直播,篮球比赛直播`,
    );
    setStatus('all');
    setTime('all');
    setVisible(PAGE_SIZE);
  }, [validLeague, league]);

  if (!validLeague) return <Navigate to="/" replace />;

  const meta = leagueMeta[validLeague];
  const allMatches = sortByTime(getMatchesByLeague(validLeague));

  const filtered = allMatches.filter((item) => {
    const statusOk = status === 'all' ? true : item.status === status;
    const timeOk = time === 'all' ? true : isWithin(item.startTime, time);
    return statusOk && timeOk;
  });

  const shown = filtered.slice(0, visible);
  const hasMore = visible < filtered.length;
  const resetFilters = () => {
    setStatus('all');
    setTime('all');
    setVisible(PAGE_SIZE);
  };

  const changeStatus = (key: string) => {
    setStatus(key);
    setVisible(PAGE_SIZE);
  };

  const changeTime = (key: string) => {
    setTime(key);
    setVisible(PAGE_SIZE);
  };

  return (
    <div className="w-full">
      <section className="w-full bg-primary-950">
        <div className="w-full px-4 md:px-6 lg:px-8 py-8 md:py-10">
          <div className="text-background-300 [&_a:hover]:text-accent-300">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: `${meta.name}赛事直播` }]} />
          </div>
          <div className="mt-4 flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div>
              <p className="font-heading text-[11px] tracking-[0.3em] text-accent-400 mb-2">
                {meta.key.toUpperCase()} · LIVE CENTER
              </p>
              <h1 className="font-heading text-2xl md:text-4xl text-background-50">
                {meta.name} 赛事直播
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-background-300">{meta.intro}</p>
            </div>
            <div className="flex items-center gap-4 md:gap-6 shrink-0">
              <Stat label="赛事总数" value={String(allMatches.length)} />
              <span className="w-px h-8 bg-primary-800" />
              <Stat label="直播中" value={String(allMatches.filter((m) => m.status === 'live').length)} accent />
              <span className="w-px h-8 bg-primary-800" />
              <Stat label="即将开始" value={String(allMatches.filter((m) => m.status === 'upcoming').length)} />
            </div>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-2">
            {leagueOrder.map((key) => (
              <Link
                key={key}
                to={`/category/${key}`}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs transition-colors whitespace-nowrap ${
                  key === meta.key
                    ? 'bg-accent-500 text-background-50'
                    : 'bg-background-50/10 text-background-200 hover:bg-background-50/20'
                }`}
              >
                <i className={leagueMeta[key].icon} />
                {leagueMeta[key].name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <div className="w-full px-4 md:px-6 lg:px-8 py-6 md:py-8">
        <div className="rounded-lg bg-background-50 border border-background-200 p-3 md:p-4">
          <div className="hidden md:flex items-center justify-between gap-4">
            <FilterGroup label="直播状态" options={statusFilters} value={status} onChange={changeStatus} />
            <FilterGroup
              label="时间范围"
              options={timeFilters}
              value={time}
              onChange={changeTime}
              variant="secondary"
            />
          </div>

          <div className="md:hidden flex items-center justify-between gap-3">
            <p className="text-sm text-foreground-800">
              已筛选 <span className="text-accent-700 font-medium">{filtered.length}</span> 场赛事
            </p>
            <button
              type="button"
              onClick={() => setPanelOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-background-300 px-3 py-2 text-xs text-foreground-800 cursor-pointer"
            >
              <i className="ri-filter-3-line" />
              筛选
            </button>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between gap-3">
          <p className="text-xs text-foreground-600">
            共 <span className="text-foreground-900 font-medium">{filtered.length}</span> 场赛事
            {time !== 'all' && ` · ${timeFilters.find((t) => t.key === time)?.label}`}
            {status !== 'all' && ` · ${statusFilters.find((s) => s.key === status)?.label}`}
          </p>
          <span className="text-xs text-foreground-400 hidden sm:inline">按开赛时间由近及远排列</span>
        </div>

        {shown.length > 0 ? (
          <>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 md:gap-4">
              {shown.map((match) => (
                <MatchCard key={match.id} match={match} />
              ))}
            </div>

            <div className="mt-6 flex flex-col items-center gap-2">
              {hasMore ? (
                <button
                  type="button"
                  onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                  className="inline-flex items-center gap-2 rounded-md bg-primary-900 px-5 py-2.5 text-sm text-background-50 hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  加载更多赛事
                  <i className="ri-arrow-down-line" />
                </button>
              ) : (
                <p className="text-xs text-foreground-400">已显示全部 {filtered.length} 场赛事</p>
              )}
            </div>
          </>
        ) : (
          <div className="mt-3 rounded-lg bg-background-50 border border-dashed border-background-300 py-14 px-6 text-center">
            <span className="w-14 h-14 mx-auto rounded-full bg-background-100 text-foreground-400 flex items-center justify-center text-2xl">
              <i className="ri-basketball-line" />
            </span>
            <h3 className="mt-3 text-base text-foreground-900">暂无该条件下的赛事</h3>
            <p className="mt-1.5 text-sm text-foreground-500">试试重置筛选，或切换到其他联赛查看</p>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex items-center gap-2 rounded-md bg-accent-500 px-4 py-2.5 text-sm font-medium text-background-50 hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line" />
                重置筛选
              </button>
              <Link
                to="/search"
                className="inline-flex items-center gap-2 rounded-md border border-background-300 px-4 py-2.5 text-sm text-foreground-800 hover:border-primary-300 transition-colors whitespace-nowrap"
              >
                搜索其他赛事
              </Link>
            </div>
          </div>
        )}

        <div className="mt-10 rounded-lg bg-background-50 border border-background-200 px-4 py-4 md:px-6 md:py-5 flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div>
            <h4 className="text-sm text-foreground-900">
              <Link to="/about" className="hover:text-primary-700 transition-colors">
                关于 {meta.name} 赛事直播与转播说明
              </Link>
            </h4>
            <p className="mt-1 text-xs text-foreground-600">
              本站赛事时间与对阵信息为演示数据，直播按钮尚未接入真实流媒体服务。
            </p>
          </div>
          <Link
            to="/category/nba"
            className="inline-flex items-center gap-1.5 text-xs text-primary-700 hover:text-primary-900 transition-colors whitespace-nowrap"
          >
            查看 NBA 直播
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>

      {panelOpen && (
        <div className="fixed inset-0 z-[60] md:hidden">
          <button
            type="button"
            aria-label="关闭筛选面板"
            onClick={() => setPanelOpen(false)}
            className="absolute inset-0 bg-foreground-950/50"
          />
          <div className="absolute bottom-0 left-0 right-0 bg-background-50 rounded-t-2xl p-4 animate-pop-in">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading text-base text-foreground-950">筛选赛事</h3>
              <button
                type="button"
                onClick={() => setPanelOpen(false)}
                className="w-8 h-8 rounded-full bg-background-100 text-foreground-700 flex items-center justify-center cursor-pointer"
                aria-label="关闭"
              >
                <i className="ri-close-line" />
              </button>
            </div>
            <FilterGroup label="直播状态" options={statusFilters} value={status} onChange={changeStatus} stacked />
            <FilterGroup label="时间范围" options={timeFilters} value={time} onChange={changeTime} variant="secondary" stacked />
            <div className="mt-4 flex items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  resetFilters();
                  setPanelOpen(false);
                }}
                className="flex-1 rounded-md border border-background-300 py-2.5 text-sm text-foreground-700 cursor-pointer"
              >
                重置
              </button>
              <button
                type="button"
                onClick={() => setPanelOpen(false)}
                className="flex-1 rounded-md bg-accent-500 py-2.5 text-sm font-medium text-background-50 cursor-pointer"
              >
                查看 {filtered.length} 场赛事
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent = false }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="flex flex-col">
      <span className={`font-heading text-2xl md:text-3xl leading-none ${accent ? 'text-accent-400' : 'text-background-50'}`}>
        {value}
      </span>
      <span className="mt-1.5 text-[11px] text-background-400">{label}</span>
    </div>
  );
}

interface FilterGroupProps {
  label: string;
  options: { key: string; label: string }[];
  value: string;
  onChange: (key: string) => void;
  variant?: 'primary' | 'secondary';
  stacked?: boolean;
}

function FilterGroup({ label, options, value, onChange, variant = 'primary', stacked = false }: FilterGroupProps) {
  return (
    <div className={stacked ? 'mb-4' : 'flex items-center gap-2 min-w-0'}>
      <span className="text-xs text-foreground-500 whitespace-nowrap mb-1.5 block md:mb-0">{label}</span>
      <div className={`inline-flex items-center gap-1 rounded-full bg-background-100 p-1 ${stacked ? 'flex-wrap' : ''}`}>
        {options.map((option) => {
          const active = option.key === value;
          const activeClass =
            variant === 'primary' ? 'bg-primary-900 text-background-50' : 'bg-accent-500 text-background-50';
          return (
            <button
              key={option.key}
              type="button"
              onClick={() => onChange(option.key)}
              className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors cursor-pointer ${
                active ? `${activeClass} font-medium` : 'text-foreground-600 hover:text-foreground-950'
              }`}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function isWithin(iso: string, range: string) {
  const target = new Date(iso).getTime();
  const now = Date.now();
  if (range === 'today') {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = start.getTime() + 24 * 60 * 60 * 1000;
    return target >= start.getTime() && target < end;
  }
  if (range === 'week') {
    const diff = target - now;
    return diff >= -7 * 24 * 60 * 60 * 1000 && diff <= 7 * 24 * 60 * 60 * 1000;
  }
  return true;
}